import { z } from 'zod';

/**
 * A multi-valued query parameter, accepted in **either** wire form and kept
 * `.optional()`-able.
 *
 * Two independent facts force this shape:
 *
 * 1. **A query string carries no arity.** One occurrence of a repeated parameter
 *    parses as a scalar, two or more as an array, so a bare `z.array(...)`
 *    rejects every single-valued request with a 400 before the handler runs —
 *    and a single select can only ever emit one value (IMM-665, BL-071).
 * 2. **This repo's own typed client comma-joins.** `buildUrl` does
 *    `params.append(key, String(value))`, so `['A', 'B']` leaves the browser as
 *    the single occurrence `?p=A,B` — never as `?p=A&p=B`. Lifting arity alone
 *    would therefore fix the one-value case and leave the many-value case
 *    failing, from the only client the app actually ships.
 *
 * So both forms are normalised: each occurrence is split on commas and the
 * occurrences are concatenated. `?p=A`, `?p=A,B`, `?p=A&p=B` and `?p=A,B&p=C`
 * all arrive at the element schema as a plain array.
 *
 * Two invariants held deliberately:
 *
 * - **`undefined` passes through untouched**, so `queryArray(...).optional()`
 *   still means „absent" and never becomes `[]` — an empty array is a filter
 *   matching nothing, which is the opposite of no filter at all. This is what
 *   separates it from {@link commaSeparated}, which normalises absent to `[]`
 *   for endpoints whose internal id set is a non-nullable array.
 * - **Element validation is untouched.** This widens how values arrive, never
 *   what counts as a value: a token that is not a member of the element schema
 *   is still rejected.
 *
 * **Use it only for element types that cannot themselves contain a comma** —
 * uuids and enum tokens, which is every array-valued query param in this
 * contract. A free-text list would need a different separator, and would be the
 * moment to reach for a repeated-only helper instead.
 *
 * The explicit return type is load-bearing: `z.preprocess` types its input as
 * `unknown`, and the typed client derives its `query` argument from
 * `z.input<…>`, so leaving it inferred would silently drop compile-time typing
 * at every call site.
 */
export function queryArray<T extends z.ZodType>(
  element: T,
): z.ZodType<z.output<T>[], z.input<T> | ReadonlyArray<z.input<T>>> {
  return z.preprocess((value) => {
    if (value === undefined) return value;

    const occurrences = Array.isArray(value) ? value : [value];

    return occurrences.flatMap((occurrence) => (typeof occurrence === 'string' ? occurrence.split(',') : [occurrence]));
  }, z.array(element)) as unknown as z.ZodType<z.output<T>[], z.input<T> | ReadonlyArray<z.input<T>>>;
}
